import { OFFLINE_MIN_PAUSE_TAGE } from '../shared/pruefpunkte.js';
import { melde } from './ergebnisse.js';
import type { StartInfo } from './geraet.js';
import { db, type TestAnlage, type TestbestandMeta } from './speicher.js';
import { dauerText, formatiereZeitpunkt, TAG_MS } from './zeit.js';

/**
 * Testbestand wie beim Pilotkunden: 2 000 Anlagen als Text und einige
 * Vorschaubilder. Eine Prüfsumme zeigt später, ob noch alles da ist.
 */
export const ANZAHL_ANLAGEN = 2_000;
export const ANZAHL_BILDER = 20;

const ARTEN = ['Feuerlöscher', 'Lüftungsgerät', 'Umwälzpumpe', 'Brandschutzklappe', 'Wärmetauscher', 'Notleuchte', 'Aufzug', 'Rauchmelder'];
const HERSTELLER = ['Minimax', 'Grundfos', 'Wilo', 'Trox', 'Viessmann', 'Schindler', 'Hekatron', 'Zumtobel'];

async function sha256Hex(daten: BufferSource): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', daten);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function testAnlage(nr: number): TestAnlage {
  const art = ARTEN[nr % ARTEN.length] ?? 'Anlage';
  const lauf = String(nr).padStart(4, '0');
  return {
    id: crypto.randomUUID(),
    nr,
    code: `HS${(nr % 7) + 1}-B-U-0${nr % 9}_AH-PU${lauf}`,
    bezeichnung: `${art} ${lauf}`,
    standort: `Liegenschaft Nord · Haus ${(nr % 7) + 1} · Ebene U · Raum 0${nr % 9}`,
    hersteller: HERSTELLER[nr % HERSTELLER.length] ?? 'unbekannt',
    modell: `Modell ${String.fromCharCode(65 + (nr % 26))}-${100 + (nr % 900)}`,
    seriennummer: `SN-${(nr * 7919).toString(36).toUpperCase()}-${lauf}`,
    baujahr: 1995 + (nr % 30),
    naechstePruefung: `2027-${String((nr % 12) + 1).padStart(2, '0')}-15`,
    verlauf: Array.from({ length: 5 }, (_, i) => ({
      datum: `202${i + 1}-0${(i % 9) + 1}-1${i}`,
      text: `Wiederkehrende Prüfung ${i + 1} durchgeführt. Sichtprüfung ohne Befund, Funktion geprüft, Plakette erneuert. Bemerkung: Zugang über Technikraum, Schlüssel beim Hausmeister. Nächste Prüfung laut Prüfpflicht in zwölf Monaten.`,
    })),
  };
}

async function vorschaubild(nr: number): Promise<Blob> {
  const leinwand = document.createElement('canvas');
  leinwand.width = 400;
  leinwand.height = 300;
  const k = leinwand.getContext('2d');
  if (!k) throw new Error('Kein Canvas verfügbar.');
  k.fillStyle = `hsl(${(nr * 37) % 360} 45% 55%)`;
  k.fillRect(0, 0, 400, 300);
  for (let i = 0; i < 60; i++) {
    k.fillStyle = `hsl(${(nr * 53 + i * 29) % 360} 50% ${30 + (i % 50)}%)`;
    k.fillRect((i * 67) % 400, (i * 41) % 300, 20 + (i % 40), 15 + (i % 30));
  }
  k.fillStyle = '#fff';
  k.font = 'bold 36px system-ui, sans-serif';
  k.fillText(`Vorschau ${nr}`, 24, 160);
  return new Promise((fertig, fehler) => leinwand.toBlob((b) => (b ? fertig(b) : fehler(new Error('Bild nicht erzeugt'))), 'image/jpeg', 0.8));
}

/** Prüfsumme über alle Anlagen (nach Nummer sortiert) und alle Vorschaubilder. */
async function berechnePruefsumme(): Promise<{ pruefsumme: string; anlagen: number; bilder: number; bytes: number }> {
  const anlagen = (await db.anlagen.toArray()).sort((a, b) => a.nr - b.nr);
  const bilder = (await db.vorschaubilder.toArray()).sort((a, b) => a.nr - b.nr);
  const text = new TextEncoder().encode(JSON.stringify(anlagen));
  const teile: Uint8Array[] = [text];
  for (const b of bilder) teile.push(new Uint8Array(await b.bild.arrayBuffer()));
  const gesamt = new Uint8Array(teile.reduce((s, t) => s + t.length, 0));
  let pos = 0;
  for (const t of teile) {
    gesamt.set(t, pos);
    pos += t.length;
  }
  return { pruefsumme: await sha256Hex(gesamt), anlagen: anlagen.length, bilder: bilder.length, bytes: gesamt.length };
}

export async function testbestand(): Promise<TestbestandMeta | undefined> {
  return db.testbestand.get('meta');
}

export async function legeTestbestandAn(fortschritt: (text: string) => void): Promise<TestbestandMeta> {
  await db.transaction('rw', db.anlagen, db.vorschaubilder, db.testbestand, async () => {
    await db.anlagen.clear();
    await db.vorschaubilder.clear();
    await db.testbestand.clear();
  });
  for (let start = 0; start < ANZAHL_ANLAGEN; start += 250) {
    fortschritt(`Anlagen ${start + 1}–${Math.min(start + 250, ANZAHL_ANLAGEN)} …`);
    await db.anlagen.bulkAdd(Array.from({ length: Math.min(250, ANZAHL_ANLAGEN - start) }, (_, i) => testAnlage(start + i + 1)));
  }
  for (let nr = 1; nr <= ANZAHL_BILDER; nr++) {
    fortschritt(`Vorschaubild ${nr} von ${ANZAHL_BILDER} …`);
    await db.vorschaubilder.add({ id: crypto.randomUUID(), nr, bild: await vorschaubild(nr) });
  }
  fortschritt('Prüfsumme wird berechnet …');
  const summe = await berechnePruefsumme();
  const meta: TestbestandMeta = { schluessel: 'meta', angelegtAm: new Date().toISOString(), ...summe };
  await db.testbestand.put(meta);
  await melde('offline-daten', 'offen', `Testbestand angelegt (${summe.anlagen} Anlagen, ${summe.bilder} Bilder). Nach mindestens ${OFFLINE_MIN_PAUSE_TAGE} Tagen ohne Nutzung erneut öffnen.`, {
    angelegtAm: meta.angelegtAm,
    anlagen: summe.anlagen,
    bilder: summe.bilder,
    bytes: summe.bytes,
  });
  return meta;
}

export interface PruefErgebnis {
  vollstaendig: boolean;
  text: string;
}

/**
 * Prüft beim Start, ob der Testbestand noch vollständig ist. Bestanden gilt
 * erst nach einer Pause von einigen Tagen ohne Nutzung, sonst bleibt es offen.
 */
export async function pruefeTestbestand(start: StartInfo, immerMelden = false): Promise<PruefErgebnis | undefined> {
  const meta = await testbestand();
  if (!meta) return undefined;
  const ist = await berechnePruefsumme();
  const jetzt = Date.now();
  const alterMs = jetzt - Date.parse(meta.angelegtAm);
  const pauseMs = start.zuletztAktivVorher ? jetzt - Date.parse(start.zuletztAktivVorher) : 0;
  const pauseTage = pauseMs / TAG_MS;
  const details = {
    angelegtAm: meta.angelegtAm,
    alterTage: Math.round((alterMs / TAG_MS) * 10) / 10,
    pauseTage: Math.round(pauseTage * 10) / 10,
    zuletztAktivVorher: start.zuletztAktivVorher,
    anlagenSoll: meta.anlagen,
    anlagenIst: ist.anlagen,
    bilderSoll: meta.bilder,
    bilderIst: ist.bilder,
  };

  if (ist.pruefsumme !== meta.pruefsumme) {
    const text = `Offline-Daten unvollständig: ${ist.anlagen} von ${meta.anlagen} Anlagen, ${ist.bilder} von ${meta.bilder} Bildern (angelegt ${formatiereZeitpunkt(meta.angelegtAm)}).`;
    await melde('offline-daten', 'fehler', text, details);
    return { vollstaendig: false, text };
  }
  const text = `Offline-Daten vollständig nach ${dauerText(pauseMs)} ohne Nutzung (angelegt vor ${dauerText(alterMs)}).`;
  if (pauseTage >= OFFLINE_MIN_PAUSE_TAGE) {
    await melde('offline-daten', 'ok', text, details);
  } else if (immerMelden) {
    await melde('offline-daten', 'offen', `${text} Für den Test mindestens ${OFFLINE_MIN_PAUSE_TAGE} Tage nicht öffnen.`, details);
  }
  return { vollstaendig: true, text };
}

export async function loescheTestbestand(): Promise<void> {
  await db.transaction('rw', db.anlagen, db.vorschaubilder, db.testbestand, async () => {
    await db.anlagen.clear();
    await db.vorschaubilder.clear();
    await db.testbestand.clear();
  });
}
